/**
 * MeshGizmo.ts
 * -----------------------------------------------------------------------------
 * Move / rotate / scale a custom mesh (Explorer ▸ Custom Meshes) directly in the
 * 3D view, with three's TransformControls.
 *
 * The bridge is ONE merged mesh (BridgeScene.setGeometry), so the selected mesh's
 * own triangles cannot follow the drag. Instead a see-through copy of them
 * (BridgeScene.buildGhost) rides on the gizmo while it is dragged; on release the
 * new placement goes to the host, which writes the mesh's X / Y / Z / Rotation /
 * Scale parameters and re-exports the scene. The host then calls `attach` again
 * with the placement it stored, so what is on screen is always what was saved.
 *
 * Frames: the viewer is metres, Y-up (model X → x, Z → y, −Y → z); the host's
 * placement is model millimetres and degrees, counter-clockwise about +Z seen
 * from above — the same sense as a turn about viewer +Y.
 *
 * The gizmo sits at the base centre of the mesh's extent, not at the file's
 * origin (which can be anywhere — an IFC's can be kilometres away). Turning and
 * scaling about that pivot moves the origin, and the arithmetic in `placement`
 * carries that through exactly, because the scale is uniform and the turn is
 * about the vertical.
 */

import * as THREE from 'three';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import type { Viewport3D } from './Viewport3D';
import type { BridgeScene } from './BridgeScene';

export type GizmoMode = 'translate' | 'rotate' | 'scale';

/** A custom mesh's placement, as the host stores it: model mm and degrees. */
export interface MeshPose {
  x: number;
  y: number;
  z: number;
  rotation: number;
  scale: number;
  /** Display length unit and how many of it make a metre, for the drag readout. */
  unit?: string;
  perMetre?: number;
}

type HostRef = { invokeMethodAsync(method: string, ...args: unknown[]): Promise<unknown> } | null;

/** TransformControls' pointer handlers: public in three's source, missing from its typings. */
type Pointer = { x: number; y: number; button: number };
type PointerApi = { pointerHover(p: Pointer): void; pointerUp(p: Pointer): void };

/** Grid steps while Shift is held. */
const SNAP_MOVE = 0.5;                            // m
const SNAP_TURN = THREE.MathUtils.degToRad(15);
const SNAP_SCALE = 0.1;

const ICONS: Record<GizmoMode, string> = {
  translate: '<path d="M8 1.5v13M1.5 8h13M8 1.5 6 3.5M8 1.5l2 2M8 14.5l-2-2M8 14.5l2-2M1.5 8l2-2M1.5 8l2 2M14.5 8l-2-2M14.5 8l-2 2"/>',
  rotate: '<path d="M13 8a5 5 0 1 1-1.6-3.7"/><path d="M11.6 1.8v2.8H8.8"/>',
  scale: '<rect x="1.5" y="6.5" width="8" height="8" rx="1"/><path d="M8.5 1.5h6v6M14.5 1.5 9 7"/>',
};
const EDIT_ICON = '<path d="M10.5 2.5l3 3L6 13H3v-3z"/><path d="M9 4l3 3"/>';

/** Whether the handles are on, remembered for the next selection and the next session. */
const EDIT_KEY = 'bridge.meshGizmo.editing';
function loadEditing(): boolean {
  try { return localStorage.getItem(EDIT_KEY) !== 'off'; } catch { return true; }
}
function saveEditing(on: boolean): void {
  try { localStorage.setItem(EDIT_KEY, on ? 'on' : 'off'); } catch { /* not remembered */ }
}

const LABELS: Record<GizmoMode, [string, string]> = {
  translate: ['Move', 'W'],
  rotate: ['Rotate', 'E'],
  scale: ['Scale', 'R'],
};

export class MeshGizmo {
  private readonly controls: TransformControls;
  /** What the gizmo moves: at the pivot, carrying the ghost offset back by it. */
  private readonly pivot = new THREE.Object3D();
  private readonly inner = new THREE.Group();
  private ghost: THREE.Group | null = null;

  private id: string | null = null;
  private pose: MeshPose | null = null;
  private readonly start = new THREE.Vector3();   // pivot at attach, viewer m
  private mode: GizmoMode = 'translate';
  /** Off: the mesh stays selected but has no handles, and a click on it is an ordinary pick. */
  private editing = loadEditing();

  private host: HostRef = null;
  /** The pointer went down on a handle: the click is the gizmo's, not a pick. */
  private grabbed = false;
  private savedControlsEnabled = true;
  private pointerInside = false;

  private readonly bar: HTMLDivElement;
  private readonly buttons = new Map<GizmoMode, HTMLButtonElement>();
  private readonly editButton: HTMLButtonElement;
  private readonly readout: HTMLSpanElement;

  private readonly onDownCapture = (e: PointerEvent): void => this.downCapture(e);
  private readonly onKey = (e: KeyboardEvent): void => this.key(e);
  private readonly onEnter = (): void => { this.pointerInside = true; };
  private readonly onLeave = (): void => { this.pointerInside = false; };

  constructor(private readonly viewport: Viewport3D, private readonly bridge: BridgeScene) {
    this.pivot.name = 'MeshGizmoPivot';
    this.pivot.userData.noOrbitPick = true;
    this.pivot.add(this.inner);

    this.controls = new TransformControls(viewport.camera, viewport.renderer.domElement);
    this.controls.name = 'MeshGizmo';
    this.controls.userData.noOrbitPick = true;
    this.controls.space = 'world';
    this.controls.size = 0.9;
    this.controls.addEventListener('change', () => viewport.invalidate());
    this.controls.addEventListener('mouseDown', () => this.begin());
    this.controls.addEventListener('objectChange', () => this.changed());
    this.controls.addEventListener('mouseUp', () => this.end());

    this.bar = document.createElement('div');
    this.bar.className = 'bridge-gizmo hidden';
    this.editButton = document.createElement('button');
    this.editButton.type = 'button';
    this.editButton.className = 'bridge-gizmo-btn bridge-gizmo-edit';
    this.editButton.innerHTML = `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">${EDIT_ICON}</svg><span>Edit in 3D</span>`;
    this.editButton.addEventListener('click', () => this.setEditing(!this.editing));
    this.bar.appendChild(this.editButton);
    for (const m of ['translate', 'rotate', 'scale'] as GizmoMode[]) {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'bridge-gizmo-btn';
      b.title = `${LABELS[m][0]} (${LABELS[m][1]})`;
      b.innerHTML = `<svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round">${ICONS[m]}</svg><span>${LABELS[m][0]}</span>`;
      b.addEventListener('click', () => this.setMode(m));
      this.bar.appendChild(b);
      this.buttons.set(m, b);
    }
    this.readout = document.createElement('span');
    this.readout.className = 'bridge-gizmo-readout';
    this.bar.appendChild(this.readout);
    viewport.container.appendChild(this.bar);

    // Capture phase on the container: runs before camera-controls' pointerdown on the canvas.
    viewport.container.addEventListener('pointerdown', this.onDownCapture, true);
    viewport.container.addEventListener('pointerenter', this.onEnter);
    viewport.container.addEventListener('pointerleave', this.onLeave);
    window.addEventListener('keydown', this.onKey);
    window.addEventListener('keyup', this.onKey);
  }

  get active(): boolean { return this.id !== null; }

  /** True when the last pointerdown landed on a handle — the click-to-select must let it be. */
  get consumedPointer(): boolean { return this.grabbed; }

  /**
   * Put the gizmo on the mesh drawn under part id `id`, at placement `pose`.
   * False (and nothing shown) when the scene has no triangles under that id —
   * the mesh is hidden, or the canvas is showing something other than the bridge.
   */
  attach(id: string, pose: MeshPose, host: HostRef): boolean {
    this.detach();
    const box = this.bridge.partsBounds([id]);
    if (!box) return false;
    const ghost = this.bridge.buildGhost([id], { color: 0x2f8cff, opacity: 0.4, edgeColor: 0x2f8cff, edgeOpacity: 0.8 });
    if (!ghost) return false;

    this.id = id;
    this.pose = pose;
    this.host = host;

    const c = box.getCenter(new THREE.Vector3());
    this.start.set(c.x, box.min.y, c.z);
    this.reset();
    this.inner.position.copy(this.start).negate();
    ghost.visible = false;
    this.ghost = ghost;
    this.inner.add(ghost);

    this.viewport.scene.add(this.pivot);
    this.setMode(this.mode);
    this.bar.classList.remove('hidden');
    this.readout.textContent = '';
    this.applyEditing();
    return true;
  }

  /** Handles on or off. The bar stays while the mesh is selected, so they can be turned back on. */
  setEditing(on: boolean): void {
    this.editing = on;
    saveEditing(on);
    this.applyEditing();
  }

  private applyEditing(): void {
    const on = this.editing && this.active;
    if (on) {
      this.viewport.scene.add(this.controls);
      this.controls.visible = true;
      if (this.controls.object !== this.pivot) this.controls.attach(this.pivot);
    } else {
      this.controls.detach();
      this.viewport.scene.remove(this.controls);
      if (this.ghost) this.ghost.visible = false;
      this.readout.textContent = '';
    }
    this.bar.classList.toggle('off', !this.editing);
    this.editButton.classList.toggle('active', this.editing);
    this.editButton.title = this.editing ? 'Turn off editing in the 3D view' : 'Move, rotate and scale the mesh in the 3D view';
    this.viewport.invalidate();
  }

  detach(): void {
    if (this.grabbed) this.restoreControls();
    this.controls.detach();
    this.viewport.scene.remove(this.controls);
    this.viewport.scene.remove(this.pivot);
    if (this.ghost) {
      this.inner.remove(this.ghost);
      this.ghost.traverse((o) => {
        const m = o as THREE.Mesh;
        m.geometry?.dispose();
        (m.material as THREE.Material | undefined)?.dispose?.();
      });
      this.ghost = null;
    }
    this.id = null;
    this.pose = null;
    this.bar.classList.add('hidden');
    this.viewport.invalidate();
  }

  /** Hide or show the handles without letting go of the mesh (thumbnail captures). */
  setVisible(on: boolean): void { this.controls.visible = on && this.active && this.editing; }

  setMode(mode: GizmoMode): void {
    this.mode = mode;
    this.controls.setMode(mode);
    // Turning is about the vertical only; X and Z rings would tilt a building over.
    this.controls.showX = mode !== 'rotate';
    this.controls.showZ = mode !== 'rotate';
    this.controls.showY = true;
    for (const [m, b] of this.buttons) b.classList.toggle('active', m === mode);
    this.viewport.invalidate();
  }

  dispose(): void {
    this.detach();
    this.viewport.container.removeEventListener('pointerdown', this.onDownCapture, true);
    this.viewport.container.removeEventListener('pointerenter', this.onEnter);
    this.viewport.container.removeEventListener('pointerleave', this.onLeave);
    window.removeEventListener('keydown', this.onKey);
    window.removeEventListener('keyup', this.onKey);
    this.controls.dispose();
    this.bar.remove();
  }

  // ---------------------------------------------------------------------------

  private reset(): void {
    this.pivot.position.copy(this.start);
    this.pivot.rotation.set(0, 0, 0);
    this.pivot.scale.setScalar(1);
    this.pivot.updateMatrixWorld(true);
  }

  private downCapture(e: PointerEvent): void {
    this.grabbed = false;
    if (!this.active || !this.editing || e.button !== 0) return;
    // The projection may have swapped cameras since the last drag.
    this.controls.camera = this.viewport.camera;
    const rect = this.viewport.renderer.domElement.getBoundingClientRect();
    const pointer = {
      x: ((e.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((e.clientY - rect.top) / rect.height) * 2 + 1,
      button: e.button,
    };
    (this.controls as unknown as PointerApi).pointerHover(pointer);
    if (this.controls.axis === null) return;
    // On a handle: the camera must not orbit with the drag. Given back on any release,
    // whether or not the press became a drag.
    this.grabbed = true;
    this.savedControlsEnabled = this.viewport.controls.enabled;
    this.viewport.controls.enabled = false;
    window.addEventListener('pointerup', () => this.restoreControls(), { once: true });
  }

  private restoreControls(): void {
    if (!this.viewport.controls.enabled) this.viewport.controls.enabled = this.savedControlsEnabled;
  }

  private begin(): void {
    if (this.ghost) this.ghost.visible = true;
    this.readout.textContent = '';
  }

  private changed(): void {
    const p = this.pivot;
    if (this.mode === 'scale') {
      // Uniform: whichever handle was pulled sets all three.
      const axis = this.controls.axis ?? 'XYZ';
      let s = axis.includes('X') ? p.scale.x : axis.includes('Y') ? p.scale.y : p.scale.z;
      s = Math.max(0.01, s);
      p.scale.setScalar(s);
    } else if (this.mode === 'rotate') {
      // The eye ring can tilt; keep only the turn about the vertical.
      const yaw = new THREE.Euler().setFromQuaternion(p.quaternion, 'YXZ').y;
      p.rotation.set(0, yaw, 0);
    }
    this.readout.textContent = this.describe();
  }

  private end(): void {
    const moved = this.pivot.position.distanceToSquared(this.start) > 1e-10
      || Math.abs(this.pivot.rotation.y) > 1e-9
      || Math.abs(this.pivot.scale.x - 1) > 1e-9;
    if (!moved || !this.id || !this.pose) {
      if (this.ghost) this.ghost.visible = false;
      this.readout.textContent = '';
      this.viewport.invalidate();
      return;
    }
    const next = this.placement();
    // The ghost stays where it was dropped until the host re-exports and re-attaches.
    void this.host?.invokeMethodAsync('OnCustomMeshTransformed', this.id, next.x, next.y, next.z, next.rotation, next.scale)
      .catch(() => { this.reset(); if (this.ghost) this.ghost.visible = false; this.viewport.invalidate(); });
  }

  /** The placement the drag has produced, in the host's frame (mm, degrees). */
  private placement(): MeshPose {
    const pose = this.pose!;
    const yaw = this.pivot.rotation.y;
    const s = this.pivot.scale.x;
    // The file origin, viewer metres, turned and scaled about the pivot, then carried with it.
    const origin = new THREE.Vector3(pose.x / 1000, pose.z / 1000, -pose.y / 1000)
      .sub(this.start)
      .multiplyScalar(s)
      .applyAxisAngle(new THREE.Vector3(0, 1, 0), yaw)
      .add(this.pivot.position);
    let rotation = pose.rotation + THREE.MathUtils.radToDeg(yaw);
    rotation = ((rotation % 360) + 540) % 360 - 180;
    return { x: origin.x * 1000, y: -origin.z * 1000, z: origin.y * 1000, rotation, scale: pose.scale * s };
  }

  private describe(): string {
    const pose = this.pose;
    if (!pose) return '';
    if (this.mode === 'rotate') {
      const d = THREE.MathUtils.radToDeg(this.pivot.rotation.y);
      return `${signed(d, 1)}°`;
    }
    if (this.mode === 'scale') {
      const s = this.pivot.scale.x;
      return `×${s.toFixed(3)}  (scale ${(pose.scale * s).toPrecision(4)})`;
    }
    const k = pose.perMetre ?? 1;
    const unit = pose.unit ?? 'm';
    const d = this.pivot.position.clone().sub(this.start);
    const decimals = k >= 100 ? 0 : 3;
    // Model axes: X = x, Y = −z, Z = y.
    return `ΔX ${signed(d.x * k, decimals)}  ΔY ${signed(-d.z * k, decimals)}  ΔZ ${signed(d.y * k, decimals)} ${unit}`;
  }

  private key(e: KeyboardEvent): void {
    if (!this.active) return;
    if (e.key === 'Shift') {
      const on = e.type === 'keydown';
      this.controls.setTranslationSnap(on ? SNAP_MOVE : null);
      this.controls.setRotationSnap(on ? SNAP_TURN : null);
      this.controls.setScaleSnap(on ? SNAP_SCALE : null);
      return;
    }
    if (e.type !== 'keydown' || !this.pointerInside || e.ctrlKey || e.altKey || e.metaKey) return;
    const t = e.target as HTMLElement | null;
    if (t && (t.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName))) return;
    const mode = ({ w: 'translate', e: 'rotate', r: 'scale' } as Record<string, GizmoMode>)[e.key.toLowerCase()];
    if (mode) {
      this.setMode(mode);
      if (!this.editing) this.setEditing(true);
      e.preventDefault();
      return;
    }
    if (e.key === 'Escape' && this.controls.dragging) {
      // Put the mesh back, then let go — end() then sees nothing moved and reports nothing.
      // TransformControls has no cancel of its own.
      this.reset();
      (this.controls as unknown as PointerApi).pointerUp({ x: 0, y: 0, button: 0 });
    }
  }
}

function signed(v: number, decimals: number): string {
  const s = v.toFixed(decimals);
  return v >= 0 ? `+${s}` : s;
}
